/**
 * Раздел HR формы «İşçini işdən çıxarma tələbi» — на шаге HR увольнения по инициативе
 * работодателя, вместе с «Согласовать»: четыре отметки проверки, статья ТК АР из справочника
 * базы 1С (подсказка — по причине из формы), согласованная дата, «взяли бы снова», срок договора
 * и примечание. Готовность считает `hrMissing`; источник правды — сервер (hrCheckError).
 */
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Text, YStack } from 'tamagui'

import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import { Button, DateField, FormInput, FormTextArea, Icon, OptionSelect, todayYmd } from '../../../ui'
import { errorText } from '../errorText'
import { HR_CHECKS, hrMissing, type HrDraft } from '../termination-types'

export function EmployerHrSection({ requestId, value, onChange }: { requestId: string; value: HrDraft; onChange: (next: HrDraft) => void }) {
  const { t } = useApp()
  const articles = useQuery({
    queryKey: ['termination-employer-articles', requestId],
    queryFn: () => api.terminationEmployerArticles(requestId),
    retry: false,
  })
  const set = (patch: Partial<HrDraft>) => onChange({ ...value, ...patch })
  // Подсказанная по причине статья — выбранной по умолчанию, пока HR не выбрал свою.
  const suggested = articles.data?.suggestedId
  useEffect(() => {
    if (suggested && !value.terminationReasonId) onChange({ ...value, terminationReasonId: suggested })
  }, [suggested]) // eslint-disable-line react-hooks/exhaustive-deps
  const missing = hrMissing(value).map((m) => t(m))

  return (
    <YStack gap={10}>
      <Text fontSize={14} fontWeight="700" color="$color">{t({ ru: 'Раздел HR', en: 'HR section', az: 'İRD əməkdaşı tərəfindən doldurulur' })}</Text>
      {HR_CHECKS.map((c) => (
        <Button
          key={c.key}
          variant={value[c.key] ? 'primary' : 'default'}
          icon={<Icon name={value[c.key] ? 'check' : 'circle'} size={15} color={value[c.key] ? '$onPrimary' : '$muted'} />}
          onPress={() => set({ [c.key]: !value[c.key] } as Partial<HrDraft>)}
        >
          {t(c.label)}
        </Button>
      ))}
      <YStack gap={4}>
        <Text fontSize={12} fontWeight="600" color="$muted">{t({ ru: 'Статья ТК АР *', en: 'Labour Code article *', az: 'ƏM əsasən maddə *' })}</Text>
        <OptionSelect
          list
          searchable
          options={(articles.data?.articles ?? []).map((a) => ({ value: a.id, label: a.text ? `${a.name} — ${a.text}` : a.name }))}
          value={value.terminationReasonId ? [value.terminationReasonId] : []}
          placeholder={articles.isPending ? '…' : t({ ru: 'Выберите статью', en: 'Choose an article', az: 'Maddəni seçin' })}
          onChange={(next) => set({ terminationReasonId: next[0] ?? '' })}
        />
        {articles.error instanceof Error && <Text fontSize={12} color="$red10">{t(errorText(articles.error))}</Text>}
      </YStack>
      <YStack gap={4}>
        <Text fontSize={12} fontWeight="600" color="$muted">{t({ ru: 'Согласованная дата увольнения *', en: 'Agreed dismissal date *', az: 'Razılaşdırılmış işdən azad olunma tarixi *' })}</Text>
        <DateField value={value.terminationDate} min={todayYmd()} onChange={(v) => set({ terminationDate: v })} />
      </YStack>
      <YStack gap={4}>
        <Text fontSize={12} fontWeight="600" color="$muted">{t({ ru: 'Взяли бы этого сотрудника снова? *', en: 'Would you rehire this employee? *', az: 'Bu işçini gələcəkdə yenidən işə götürərdinizmi? *' })}</Text>
        <OptionSelect
          options={[{ value: 'yes', label: t({ ru: 'Да', en: 'Yes', az: 'Hə' }) }, { value: 'no', label: t({ ru: 'Нет', en: 'No', az: 'Yox' }) }]}
          value={value.rehire ? [value.rehire] : []}
          onChange={(next) => set({ rehire: (next[0] ?? '') as HrDraft['rehire'] })}
        />
      </YStack>
      <YStack gap={4}>
        <Text fontSize={12} fontWeight="600" color="$muted">{t({ ru: 'Сведения о сроке трудового договора', en: 'Employment contract term', az: 'Müqavilənin bitmə tarixi haqqında məlumat' })}</Text>
        <FormInput value={value.contractEndInfo} maxLength={500} onChangeText={(v) => set({ contractEndInfo: v })} />
      </YStack>
      <YStack gap={4}>
        <Text fontSize={12} fontWeight="600" color="$muted">{t({ ru: 'Примечание HR', en: 'HR notes', az: 'Əlavə qeydlər' })}</Text>
        <FormTextArea value={value.notes} maxLength={4000} onChangeText={(v) => set({ notes: v })} />
      </YStack>
      {!!missing.length && (
        <Text fontSize={12} color="$muted">
          {t({ ru: 'Чтобы согласовать, заполните: ', en: 'To approve, fill in: ', az: 'Razılaşdırmaq üçün doldurun: ' }) + missing.join(', ')}
        </Text>
      )}
    </YStack>
  )
}
